import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { releaseHero, setAnchorNavigating, tweenScrollTo } from '@/lib/scrollOwnership';

const HEADER_OFFSET = 88;

const links: { id: string; label: string }[] = [
  { id: 'services', label: 'Services' },
  { id: 'approach', label: 'Our Approach' },
  { id: 'team',     label: 'Team' },
  { id: 'visit',    label: 'Visit Us' },
];

async function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  // 1) release Hero BEFORE starting the tween
  releaseHero();
  setAnchorNavigating(true);
  document.documentElement.classList.add('is-anchor-navigating');
  // wait a frame so Hero listener processes release before we start moving
  await new Promise<void>((r) => requestAnimationFrame(() => r()));
  const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  await tweenScrollTo(y, 720);
  // small settle delay so the element under the cursor doesn't fire a hover spike
  await new Promise<void>((r) => requestAnimationFrame(() => setTimeout(r, 40)));
  document.documentElement.classList.remove('is-anchor-navigating');
  setAnchorNavigating(false);
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState<string>('');
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  // Scrollspy — only when on home
  useEffect(() => {
    if (loc.pathname !== '/') { setActiveId(''); return; }
    const sections = links
      .map(l => document.getElementById(l.id))
      .filter((el): el is HTMLElement => !!el);
    if (!sections.length) return;

    // Cache section offsets to avoid repeated getBoundingClientRect/layout thrash.
    let offsets: { id: string; top: number }[] = [];
    const measure = () => {
      offsets = sections.map(s => ({ id: s.id, top: s.offsetTop }));
    };
    measure();
    const update = () => {
      const line = window.scrollY + HEADER_OFFSET + 40;
      let current = '';
      for (const s of offsets) {
        if (s.top <= line) current = s.id;
      }
      setActiveId(prev => (prev === current ? prev : current));
    };
    update();
    let ticking = false;
    const onScroll = () => {
      // During programmatic anchor navigation, do not run per-frame React updates.
      if (document.documentElement.classList.contains('is-anchor-navigating')) return;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => { update(); ticking = false; });
      }
    };
    const onResize = () => { measure(); onScroll(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [loc.pathname]);

  // Handle initial hash scroll after route change
  useEffect(() => {
    if (loc.hash) {
      const id = loc.hash.slice(1);
      // wait a frame for content mount
      requestAnimationFrame(() => {
        setTimeout(() => { void scrollToId(id); }, 40);
      });
    }
  }, [loc.pathname, loc.hash]);

  const onNavClick = useCallback((e: React.MouseEvent, id: string) => {
    e.preventDefault();
    if (loc.pathname === '/') {
      setActiveId(id); // mark destination active up-front; scrollspy stays quiet during tween
      window.history.replaceState(null, '', `/#${id}`);
      void scrollToId(id);
    } else {
      navigate(`/#${id}`);
    }
    setOpen(false);
  }, [loc.pathname, navigate]);

  return (
    <header className="sticky top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-b border-line">
      <div className="max-w-container mx-auto px-5 md:px-12 h-[72px] flex items-center justify-between">
        <Link to="/" className="font-display text-[22px] font-semibold tracking-tight cursor-pointer text-cocoa">
          <span className="inline-flex items-center gap-2">
            <span className="w-8 h-8 rounded-full flex items-center justify-center bg-cocoa text-white">
              <i className="fa-solid fa-paw text-[15px]"></i>
            </span>
            Alder Vet
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map(l => {
            const isActive = activeId === l.id;
            return (
              <a
                key={l.id}
                href={`/#${l.id}`}
                onClick={(e) => onNavClick(e, l.id)}
                aria-current={isActive ? 'page' : undefined}
                className={`nav-underline text-[15px] font-medium cursor-pointer whitespace-nowrap transition-colors text-ink/80 hover:text-ink active:scale-[.98] ${isActive ? '!text-ink is-active' : ''}`}
              >
                {l.label}
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/book" className="btn-motion hidden md:inline-flex items-center gap-2 h-[44px] px-5 rounded-full bg-cocoa hover:bg-careDark text-white text-[14px] font-semibold whitespace-nowrap cursor-pointer">
            Book an Appointment
            <i className="ri-arrow-right-up-line text-[16px]"></i>
          </Link>
          <button aria-label="Menu" onClick={() => setOpen(!open)} className="md:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer text-ink">
            <i className={`${open ? 'ri-close-line' : 'ri-menu-line'} text-[22px]`}></i>
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-line px-5 py-6">
          <div className="flex flex-col gap-4">
            {links.map(l => (
              <a
                key={l.id}
                href={`/#${l.id}`}
                onClick={(e) => onNavClick(e, l.id)}
                className={`text-ink text-[17px] font-medium cursor-pointer ${activeId === l.id ? 'text-cocoa' : ''}`}
              >
                {l.label}
              </a>
            ))}
            <Link to="/book" className="btn-motion mt-2 inline-flex items-center justify-center h-[50px] rounded-[14px] bg-care text-white font-semibold cursor-pointer whitespace-nowrap">
              Book an Appointment
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}